import { ChangeRequestType } from "../../../requests_cm/gepoChangeRequestService/types";

import ErrorMessageModal from "../../../common/components/ErrorMessageDialog";
import { useHorizonChanges } from "../../../common/hooks/useHandleHorizonChanges";
import { Box, Typography } from "@mui/material";
import { camelToSpaceCase } from "../../../common/utils/camelToSpaceCase";
import { HorizonChangeType } from "../../../requests_cm/gecoContractsService/types";
import { HorizonUnitHOC } from "../../horizon_change/HorizonMonitoringStep/HorizonUnit";
import { noop } from "../../../common/utils/operations";
import { Spacer } from "../../../common/components/Spacer";
import theme from "../../../core/theme";
import { forwardRef, useCallback, useEffect, useImperativeHandle } from "react";
import { useUpdateContractPeriodHorizonChangeMutation } from "../../../requests_cm/gecoContractsService/service";
import { useRtkQueryDynamicEndpoint } from "../../../common/hooks/useRtkQueryDynamicEndpoint";

export const style = {
	body: {
		title: {
			fontFamily: theme.typography.fontFamily,
			fontSize: "18px",
			fontWeight: "600",
		},
	},
	row: {
		display: "flex",
		flexDirection: "column",
		gap: "16px",
		list: {
			display: "flex",
			gap: "16px",
			flexDirection: "row",
			flexWrap: "wrap",
		},
		title: {
			fontWeight: "800",
			fontSize: "16px",
		},
	},
};

export interface HorizonChangeValidationProps {
	changeRequest: ChangeRequestType | undefined;
	onConfirmSuccess: () => void;
}

// eslint-disable-next-line react/display-name
export const HorizonChangeValidation = forwardRef<HorizonChangeValidationProps>(
	(props, ref) => {
		// eslint-disable-next-line react/prop-types
		const { changeRequest, onConfirmSuccess } =
			props as HorizonChangeValidationProps;

		// eslint-disable-next-line react/prop-types
		const { raw_json: rawJson, resource_id: resourceId } =
			changeRequest as ChangeRequestType;
		const { entities, entitiesMap } = useHorizonChanges(rawJson || []);

		const [
			baseUpdateHorizonChange,
			{ error: errorUpdate, isSuccess: isUpdateSuccess },
		] = useUpdateContractPeriodHorizonChangeMutation();

		const updateHorizonChange = useRtkQueryDynamicEndpoint(
			baseUpdateHorizonChange
		);

		const onConfirmUpdate = useCallback(() => {
			if (changeRequest) {
				updateHorizonChange({
					contractPeriod_id: resourceId,
					proposed_changes: rawJson,
				});
			}
		}, [changeRequest]);

		useImperativeHandle(ref, () => ({
			// the onConfirmUpdate is the important bit
			onConfirmUpdate,
			changeRequest,
			onConfirmSuccess,
		}));

		useEffect(() => {
			if (isUpdateSuccess) {
				onConfirmSuccess();
			}
		}, [isUpdateSuccess]);

		return (
			<>
				<Typography sx={style.body.title}>Impacted elements</Typography>
				<Spacer gap={24} />

				{(entities || []).map((entity) => (
					<>
						<Box key={entity} sx={style.row}>
							<Typography sx={style.row.title}>
								{camelToSpaceCase(entity)}
							</Typography>
							<Box sx={style.row.list}>
								{entitiesMap[entity]?.map(
									(horizonChange: HorizonChangeType) => (
										<HorizonUnitHOC
											key={`${horizonChange?.target_id}-${horizonChange?.target_name}`}
											name={horizonChange.target_name}
											onUpdate={noop}
											initialValues={horizonChange}
											readonly
										/>
									)
								)}
							</Box>
						</Box>
						<Spacer gap={24} />
					</>
				))}
				<ErrorMessageModal
					title={`We have encoutered an issue for horizon change update with the following error.`}
					content={errorUpdate}
				/>
			</>
		);
	}
);
